import { APP_INITIALIZER, Injectable, Optional, Provider } from '@angular/core';
import { AppCheck, getToken, onTokenChanged } from '@angular/fire/app-check';
import { Auth } from '@angular/fire/auth';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { warnToServer } from '../../shared/services/server-warn';

export type AppCheckState = 'none' | 'pending' | 'ok' | 'error';

/** Marker-String fuer die Error-Triage (known-errors.yaml) - exakt so, nicht umformulieren. */
export const APP_CHECK_TOKEN_ERROR_MESSAGE = 'AppCheck: token error';

/**
 * Telemetrie fuer Firebase App Check (Workorder app-check-rtdb 2026-10-26, Task 2): abonniert einmal im App-Start
 * `onTokenChanged` und meldet den ersten Token-Fehler der Session als `warn` nach `/uilogging`. Nach dem Prod-Deploy
 * zeigt der Anteil dieser Zeile in Cloud Logging, in wie vielen Browsern reCAPTCHA nicht lief (In-App-Browser,
 * Blocker, Proxys) - das zweite Signal neben den Console-Metriken fuer den Enforcement-Entscheid (Runbook M7).
 *
 * Der Service blockiert nichts. `start()` ruft einmal selbst `getToken(appCheck)` (nicht awaited), nur um die Zeit zu
 * messen, zu der das Token vorlag (`tokenAtMs`, Workorder app-check-connection-stalled-telemetry 2026-10-27, Entscheid 5):
 * das SDK teilt parallele Exchanges ueber `state.exchangeTokenPromise` (`getToken$2` in `@firebase/app-check`), der
 * Aufruf haengt sich also an den Tausch des Token-Refreshers bzw. der RTDB an und deckt auch den IndexedDB-Cache-Pfad
 * ab, den `onTokenChanged` nicht immer meldet. Kein zweiter reCAPTCHA-Lauf. `getToken` wirft im Fehlerfall (das SDK
 * reicht intern einen Dummy-Token weiter) - `tokenAtMs` wird bei Erfolg und Fehler gesetzt, `state` folgt weiter
 * nur `onTokenChanged`. `authAtMs` ist die Zeit, zu der Firebase Auth initialisiert war (`authStateReady()`), die
 * zweite Voraussetzung, auf die die RTDB vor dem WebSocket wartet.
 *
 * Server-Level: dev/prod senden nur ERROR und hoeher an `/uilogging` (`ngxServerLoggerLevel: 5`). Der eine Warn
 * dieser Session muss trotzdem ankommen, ohne das Level global zu senken - `warnToServer` (shared/services/server-warn.ts)
 * stellt `serverLogLevel` nur fuer diesen Aufruf auf WARN.
 *
 * `state` ('none' | 'pending' | 'ok' | 'error') ist der App-Check-Stand fuer andere Diagnosen, z. B. den
 * ConnectionStalled-Report (workorder-app-check-connection-stalled-hint-2026-10-26).
 *
 * Fehlerdetails (workorder-app-check-recaptcha-preload-telemetry-2026-10-29, Entscheid 5): das SDK wirft jeden Nicht-2xx
 * des Exchange-Endpoints als `appCheck/throttled` (`ReCaptchaEnterpriseProvider.getToken` -> `setBackoff`), 403
 * (reCAPTCHA-Assessment abgelehnt, typisch Headless/Bots) genauso wie 429/503. Den Status und die Backoff-Dauer traegt
 * nur `customData` (`httpStatus`, `time`). Der Warn-Detail-Text ist deshalb `<code> [<httpStatus>] [<time>]`, `lastError`
 * (`<code> [<httpStatus>]`, bei jedem Fehler aktualisiert) geht in die ConnectionStalled-Reports.
 */
@Injectable({ providedIn: 'root' })
export class AppCheckTelemetryService {
    private started = false;
    private errorReported = false;
    private okReported = false;
    private _state: AppCheckState = 'none';
    private _tokenAtMs: number | null = null;
    private _authAtMs: number | null = null;
    private _lastError: string | null = null;

    constructor(
        @Optional() private appCheck: AppCheck | null,
        private logger: LoggerService,
        @Optional() private auth: Auth | null = null,
    ) { }

    /** 'none' = App Check nicht initialisiert, 'pending' = noch kein Token, 'ok' = letztes Ereignis ein Token, 'error' = letztes Ereignis ein Fehler */
    get state(): AppCheckState {
        return this._state;
    }

    /** performance.now(), zu dem der eigene `getToken()`-Aufruf aufgeloest hat (Token oder Fehler); null solange nicht bzw. ohne App Check */
    get tokenAtMs(): number | null {
        return this._tokenAtMs;
    }

    /** performance.now(), zu dem Firebase Auth initialisiert war (`authStateReady()`); null solange nicht */
    get authAtMs(): number | null {
        return this._authAtMs;
    }

    /** letzter Token-Fehler der Session als `<code>` bzw. `<code> <httpStatus>` (z. B. `appCheck/throttled 403`); null ohne Fehler */
    get lastError(): string | null {
        return this._lastError;
    }

    /** idempotent; ohne initialisiertes App Check (leerer siteKey) wird nur die Auth-Zeit gemessen */
    start(): void {
        if (this.started) return;
        this.started = true;
        this.measureAuthReady();
        if (!this.isInitialized(this.appCheck)) {
            this.logger.debug('AppCheck: not initialized, no telemetry');
            return;
        }
        this._state = 'pending';
        this.measureToken(this.appCheck);
        try {
            onTokenChanged(
                this.appCheck,
                () => this.onToken(),
                (error: unknown) => this.onError(error),
            );
        } catch (error) {
            this.logger.debug('AppCheck: telemetry subscription failed', (error as Error)?.message);
        }
    }

    private measureToken(appCheck: AppCheck): void {
        const settle = () => { if (this._tokenAtMs === null) this._tokenAtMs = performance.now(); };
        try {
            getToken(appCheck).then(settle, settle);
        } catch (error) {
            this.logger.debug('AppCheck: getToken for the token time failed', (error as Error)?.message);
        }
    }

    private measureAuthReady(): void {
        const ready = (this.auth as { authStateReady?: () => Promise<void> } | null)?.authStateReady;
        if (typeof ready !== 'function') return;
        const settle = () => { if (this._authAtMs === null) this._authAtMs = performance.now(); };
        try {
            ready.call(this.auth).then(settle, settle);
        } catch (error) {
            this.logger.debug('AppCheck: authStateReady failed', (error as Error)?.message);
        }
    }

    /** AngularFire liefert bei leerem Factory-Ergebnis ein leeres Wrapper-Objekt statt null - auf `app` pruefen */
    private isInitialized(appCheck: AppCheck | null): appCheck is AppCheck {
        return !!appCheck && !!(appCheck as { app?: unknown }).app;
    }

    private onToken(): void {
        this._state = 'ok';
        if (this.okReported) return;
        this.okReported = true;
        this.logger.debug('AppCheck: token ok');
    }

    /** Warn einmal pro Session, nur Code bzw. Message plus Status und Backoff - kein Stack, keine Nutzerdaten */
    private onError(error: unknown): void {
        this._state = 'error';
        const { code, httpStatus, time } = describeTokenError(error);
        this._lastError = httpStatus === null ? code : `${code} ${httpStatus}`;
        if (this.errorReported) return;
        this.errorReported = true;
        const detail = time === null ? this._lastError : `${this._lastError} ${time}`;
        warnToServer(this.logger, APP_CHECK_TOKEN_ERROR_MESSAGE, detail);
    }
}

/** Code (bzw. Message) und die `customData` eines App-Check-Fehlers; alles optional, fehlende Teile null */
export function describeTokenError(error: unknown): { code: string; httpStatus: number | null; time: string | null } {
    const { code, message, customData } = (error ?? {}) as { code?: unknown; message?: unknown; customData?: { httpStatus?: unknown; time?: unknown } };
    const rawStatus = customData?.httpStatus;
    const httpStatus = typeof rawStatus === 'number' || (typeof rawStatus === 'string' && rawStatus !== '') ? Number(rawStatus) : NaN;
    const time = customData?.time;
    return {
        code: typeof code === 'string' ? code : typeof message === 'string' ? message : String(error),
        httpStatus: Number.isFinite(httpStatus) ? httpStatus : null,
        time: typeof time === 'string' && time !== '' ? time : null,
    };
}

export function startAppCheckTelemetry(service: AppCheckTelemetryService): () => void {
    return () => service.start();
}

/** im App-Start registrieren (app.module.ts providers) - laeuft nicht-blockierend vor dem Bootstrap */
export const APP_CHECK_TELEMETRY_INITIALIZER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: startAppCheckTelemetry,
    deps: [AppCheckTelemetryService],
    multi: true,
};
